import { useCallback, useEffect, useRef } from 'react';

import type { NavigateOptions } from 'react-router';

import { getDocument } from '@change-corgi/core/window';
import { useNavigate } from '@change-corgi/core-react/router';

import type { CancelableEventDetail, CancelableEventName } from '#src/shared/customEvents';
import { emitCancelableEvent } from '#src/shared/customEvents';

type CancelableNavigateEventNames = Extract<
	CancelableEventName,
	'shareChannelSelectedRedirect' | 'postSignatureRedirect'
>;

type CancelableNavigateConfig = {
	[T in CancelableNavigateEventNames]: {
		eventName: T;
		eventConfig: Omit<CancelableEventDetail<T>, 'originalRedirectFn'>;
		redirectUrl: string;
		navigateOptions?: NavigateOptions & {
			forceMode?: 'internal' | 'external';
		};
		/**
		 * Hold the navigation until the document is visible again, instead of running it against a
		 * hidden page.
		 *
		 * Needed when the navigation follows an action that hands off to another app — a share to
		 * Mail, Messages or X. The OS backgrounds the webview as the handoff starts, and a route that
		 * navigates by full page load (`ssr.isolated`) then begins a document load the webview
		 * immediately suspends. The supporter returns to a blank page that never finishes loading and
		 * has to reload by hand (CHANGE-85435).
		 */
		deferWhileHidden?: boolean;
	};
}[CancelableNavigateEventNames];

/**
 * A hook that provides cancelable navigation functionality.
 *
 * This hook allows you to emit a cancelable event before navigation occurs.
 * If any event listener cancels the event, the navigation will be prevented.
 * Otherwise, the navigation proceeds as normal.
 *
 * @returns An object containing the cancelableNavigate function
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { cancelableNavigate } = useCancelableNavigate();
 *
 *   const handleNavigation = () => {
 *     cancelableNavigate({
 *       redirectUrl: '/new-page',
 *       eventName: 'shareChannelSelectedRedirect',
 *       eventConfig: {
 *         // Additional event-specific configuration
 *         someProperty: 'value'
 *       },
 *       navigateOptions: { forceMode: 'external' } // optional
 *     });
 *   };
 *
 *   return <button onClick={handleNavigation}>Navigate</button>;
 * }
 * ```
 */
export function useCancelableNavigate(): {
	/**
	 * Performs a cancelable navigation operation.
	 *
	 * Emits a cancelable event with the provided configuration. If the event
	 * is not cancelled by any listener, navigation to the specified URL occurs.
	 * The event detail automatically includes an `originalRedirectFn` that can
	 * be called by event listeners to perform the original navigation.
	 *
	 * @param config - Configuration object for the cancelable navigation
	 * @param config.redirectUrl - The URL to navigate to if not cancelled
	 * @param config.eventName - The name of the cancelable event to emit
	 * @param config.eventConfig - Additional event configuration (originalRedirectFn is added automatically)
	 * @param config.navigateOptions - Optional navigate options (e.g., forceMode, replace, state)
	 * @param config.deferWhileHidden - Wait for the document to be visible before navigating
	 */
	cancelableNavigate: (config: CancelableNavigateConfig) => void;
} {
	const navigate = useNavigate();

	const unwatchVisibilityRef = useRef<(() => void) | undefined>(undefined);

	const clearPendingVisibilityWatch = useCallback(() => {
		unwatchVisibilityRef.current?.();
		unwatchVisibilityRef.current = undefined;
	}, []);

	useEffect(() => clearPendingVisibilityWatch, [clearPendingVisibilityWatch]);

	const navigateWhenVisible = useCallback(
		(performNavigate: () => void) => {
			// A second navigation supersedes whatever the first was still waiting for.
			clearPendingVisibilityWatch();

			const doc = getDocument();
			if (doc.visibilityState !== 'hidden') {
				performNavigate();
				return;
			}

			const onVisibilityChange = () => {
				if (getDocument().visibilityState === 'hidden') return;
				clearPendingVisibilityWatch();
				performNavigate();
			};
			doc.addEventListener('visibilitychange', onVisibilityChange);
			unwatchVisibilityRef.current = () => doc.removeEventListener('visibilitychange', onVisibilityChange);
		},
		[clearPendingVisibilityWatch],
	);

	const cancelableNavigate = useCallback(
		({ eventName, eventConfig, redirectUrl, navigateOptions, deferWhileHidden }: CancelableNavigateConfig) => {
			const performNavigate = () => navigate(redirectUrl, navigateOptions);
			// Listeners get the same deferral as the default path — an `originalRedirectFn` that ignored it
			// would reintroduce the blank page for anything that cancels and re-triggers the navigation.
			const runNavigate = deferWhileHidden ? () => navigateWhenVisible(performNavigate) : performNavigate;

			const isNavigationCancelled = !emitCancelableEvent(eventName, {
				...eventConfig,
				originalRedirectFn: runNavigate,
			});
			if (isNavigationCancelled) {
				return;
			}
			runNavigate();
		},
		[navigate, navigateWhenVisible],
	);

	return {
		cancelableNavigate,
	};
}
